import type { CSSProperties, HTMLAttributes } from 'react';

export type ProgressVariant = 'bar' | 'steps';

export interface ProgressIndicatorProps extends Omit<HTMLAttributes<HTMLDivElement>, 'style'> {
  readonly value?: number;
  readonly total?: number;
  readonly label?: string;
  /** Nom accessible du widget `progressbar` (identité stable) — distinct du `label` visible, qui peut varier (message d'encouragement). */
  readonly accessibleLabel?: string;
  readonly showCount?: boolean;
  readonly variant?: ProgressVariant;
  /** Passe en typographie mobile agrandie (label + compteur) pour les écrans compacts (≤ 600 px).
   *  Voir revue Android 412px : les tailles desktop (--fs-micro / --fs-label) étaient illisibles. */
  readonly compact?: boolean;
  readonly style?: CSSProperties;
}

/** Indicateur de progression : filet fin, jamais un score ni un pourcentage criard. */
export function ProgressIndicator({
  value = 0,
  total = 4,
  label,
  accessibleLabel,
  showCount = true,
  variant = 'bar',
  compact = false,
  style,
  ...rest
}: ProgressIndicatorProps) {
  const pct = total ? Math.max(0, Math.min(1, value / total)) : 0;
  const progressProps = {
    role: 'progressbar' as const,
    'aria-valuenow': Math.round(pct * 100),
    'aria-valuemin': 0,
    'aria-valuemax': 100,
    ...(accessibleLabel ? { 'aria-label': accessibleLabel } : {}),
  };
  if (variant === 'steps') {
    return (
      <div {...rest} {...progressProps} style={{ display: 'flex', alignItems: 'center', gap: 6, ...style }}>
        {Array.from({ length: total }).map((_, index) => (
          <span
            key={index}
            style={{
              width: 26,
              height: 3,
              borderRadius: 2,
              background: index < value ? 'var(--accent)' : 'rgba(255,255,255,.12)',
              transition: 'background var(--dur-base) var(--ease-calm)',
            }}
          />
        ))}
        {showCount && (
          <span
            style={{
              marginLeft: 8,
              fontFamily: 'var(--font-ui)',
              fontSize: 11,
              letterSpacing: '.08em',
              color: 'var(--text-muted)',
            }}
          >
            {value} / {total}
          </span>
        )}
      </div>
    );
  }
  return (
    <div {...rest} {...progressProps} style={{ display: 'grid', gap: 8, ...style }}>
      {label && (
        <span
          style={{
            fontFamily: 'var(--font-ui)',
            fontSize: compact ? 'var(--fs-label)' : 10,
            letterSpacing: 'var(--tracking-label)',
            textTransform: 'uppercase',
            textAlign: 'center',
            color: 'var(--text-muted)',
          }}
        >
          {label}
        </span>
      )}
      <span
        style={{
          display: 'block',
          height: 2,
          background: 'rgba(255,255,255,.10)',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <span
          style={{
            display: 'block',
            height: '100%',
            width: `${pct * 100}%`,
            background: 'var(--accent)',
            transition: 'width var(--dur-slow) var(--ease-calm)',
          }}
        />
      </span>
      {showCount && (
        <span style={{ fontFamily: 'var(--font-ui)', fontSize: compact ? 'var(--fs-small)' : 11, color: 'var(--text-faint)' }}>
          {value} sur {total}
        </span>
      )}
    </div>
  );
}
